import { useState } from "react";
import { PageHeader } from "../components/layout/PageHeader";
import { VIDEO_STYLES, VIDEO_FORMATS, RECENT_VIDEOS } from "../lib/sample-data";
import { Film, Wand2, Upload, Play, Sparkles, Mic, UserSquare2, ShoppingBag } from "lucide-react";
import { cn } from "../lib/utils";

const VIDEO_TYPES = [
  { id: "product", label: "Product Video", icon: ShoppingBag },
  { id: "avatar", label: "Avatar Video", icon: UserSquare2 },
  { id: "promo", label: "Promo Video", icon: Film },
  { id: "short", label: "Short-form Ad", icon: Sparkles },
];

export default function VideoLab() {
  const [type, setType] = useState("product");
  const [style, setStyle] = useState("cinematic");
  const [script, setScript] = useState("");

  return (
    <div data-testid="video-lab-page" className="space-y-8">
      <PageHeader
        kicker="VideoLab"
        title="AI video generation"
        description="Create product videos, avatar videos, promos, and short-form ads. Inspired by Higgsfield, HeyGen, and product-to-video workflows."
        actions={
          <button
            data-testid="video-generate-btn"
            className="inline-flex items-center gap-2 px-4 h-11 rounded-xl pl-btn-aqua text-sm"
          >
            <Wand2 className="h-4 w-4" /> Generate Video
          </button>
        }
      />

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="video-types">
        {VIDEO_TYPES.map((t) => {
          const I = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`video-type-${t.id}`}
              onClick={() => setType(t.id)}
              className={cn(
                "rounded-2xl p-5 text-left border pl-lift",
                type === t.id
                  ? "border-[var(--pl-aqua)] bg-[var(--pl-aqua)]/10"
                  : "border-[var(--pl-border)] bg-[var(--pl-surface)]/60"
              )}
            >
              <I className="h-5 w-5 text-[var(--pl-aqua)]" />
              <div className="font-medium text-[var(--pl-text)] mt-2">{t.label}</div>
              <div className="text-[11px] text-[var(--pl-text-muted)]">From prompt, image, or product URL</div>
            </button>
          );
        })}
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Script / Prompt</div>
            <textarea
              data-testid="video-script"
              rows={6}
              value={script}
              onChange={(e) => setScript(e.target.value)}
              placeholder="What should this video say? Describe scenes, hooks, and the call to action."
              style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
              className="mt-2 w-full rounded-xl border border-[var(--pl-border)] p-3 text-sm placeholder:text-[var(--pl-text-muted)] focus:outline-none focus:border-[var(--pl-aqua)]/50"
            />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Scene Style</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {VIDEO_STYLES.map((s) => (
                <button
                  key={s.id}
                  data-testid={`video-style-${s.id}`}
                  onClick={() => setStyle(s.id)}
                  className={cn(
                    "px-3 h-9 rounded-full text-xs border transition",
                    style === s.id
                      ? "bg-[var(--pl-aqua)]/15 border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)]"
                      : "border-[var(--pl-border)] text-[var(--pl-text-muted)] hover:text-[var(--pl-text)]"
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <UploadCard testId="video-product-upload" icon={ShoppingBag} title="Product Image" desc="Upload product photo or paste URL" />
            <UploadCard testId="video-avatar-upload" icon={UserSquare2} title="Avatar" desc="Pick an avatar or upload reference" />
          </div>

          <div className="rounded-xl border border-[var(--pl-aqua)]/30 bg-[var(--pl-aqua)]/5 p-4">
            <div className="flex items-center gap-2 text-[var(--pl-aqua)] text-[11px]">
              <Sparkles className="h-3.5 w-3.5" /> Routed to Higgsfield (cinematic) · HeyGen (avatar) when connected.
            </div>
            <div className="text-xs text-[var(--pl-text-muted)] mt-1">
              Connect providers in Integrations → Creative AI APIs.
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Generate across formats</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {VIDEO_FORMATS.map((f) => (
                <button
                  key={f.id}
                  data-testid={`video-format-${f.id}`}
                  className="rounded-xl px-3 py-2 text-xs border border-[var(--pl-border)] bg-white/[0.03] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40"
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Voice</div>
            <div className="flex items-center gap-2 mt-2">
              <Mic className="h-4 w-4 text-[var(--pl-aqua)]" />
              <select
                data-testid="video-voice"
                style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
                className="flex-1 rounded-xl border border-[var(--pl-border)] px-3 h-10 text-sm focus:outline-none focus:border-[var(--pl-aqua)]/50"
              >
                <option>Premium Male · US</option>
                <option>Premium Female · US</option>
                <option>Founder · Calm</option>
                <option>Hype · Sharp</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Recent Videos</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {RECENT_VIDEOS.map((v) => (
            <div
              key={v.id}
              data-testid={`recent-video-${v.id}`}
              className="rounded-2xl overflow-hidden border border-[var(--pl-border)] bg-[var(--pl-surface)]/70 pl-lift"
            >
              <div className="aspect-video bg-[var(--pl-bg-2)] relative overflow-hidden">
                <img src={v.thumb} alt={v.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 grid place-items-center bg-black/30 opacity-0 hover:opacity-100 transition">
                  <div className="h-12 w-12 rounded-full pl-btn-aqua grid place-items-center">
                    <Play className="h-5 w-5" />
                  </div>
                </div>
              </div>
              <div className="p-3">
                <div className="text-sm font-medium text-[var(--pl-text)] truncate">{v.title}</div>
                <div className="text-[11px] text-[var(--pl-text-muted)] font-mono mt-0.5">{v.meta}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function UploadCard({ icon: I, title, desc, testId }) {
  return (
    <button
      data-testid={testId}
      className="rounded-xl border border-dashed border-[var(--pl-border)] p-4 flex items-center gap-3 hover:border-[var(--pl-aqua)]/50 transition text-left"
    >
      <div className="h-10 w-10 rounded-xl bg-[var(--pl-aqua)]/10 border border-[var(--pl-aqua)]/30 grid place-items-center">
        <I className="h-5 w-5 text-[var(--pl-aqua)]" />
      </div>
      <div>
        <div className="text-sm font-medium text-[var(--pl-text)]">{title}</div>
        <div className="text-[11px] text-[var(--pl-text-muted)]">{desc}</div>
      </div>
      <Upload className="h-4 w-4 text-[var(--pl-text-muted)] ml-auto" />
    </button>
  );
}
